import React, { Component } from 'react';
import clsx from 'clsx';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { CssBaseline, CircularProgress, Container, Tabs, Tab, Box, Button, Chip, IconButton, TextField, Switch, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Pagination, Alert, FormControlLabel, Skeleton } from '@mui/material';
import { withStyles } from '@mui/styles';
import { Styles } from '../../styles/material-styles';
import { Overlay } from '../../styles/global';
import CustomizedSnackbars from '../../components/material-snackbars';
import HeaderMenu from '../../components/header-menu';
import Footer from '../../components/footer';
import Api from '../../services/api';
import AppConnectorService, { normalizeEndpoint, normalizeEvent, normalizeProfile, normalizeProviderEvent, normalizeTemplate } from '../../services/appconnector.service';
import { injectIntl } from 'react-intl';
import styles from '../../styles/appconnector-styles';
import * as componentActions from '../../redux/actions/header-menu';
import { ArrowLeft, Plus, RefreshCw, Copy, Play, SquareChartGantt, Edit, Save, Settings } from 'lucide-react';
import moment from 'moment';
import StatusChip from '../../components/app-connector/StatusChip';
import CopyableField from '../../components/app-connector/CopyableField';
import PayloadViewer from '../../components/app-connector/PayloadViewer';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ptBR from 'date-fns/locale/pt-BR';

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (<div role="tabpanel" hidden={value !== index} {...other}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>);
}

class AppConnectorEndpointDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: false,
      openNotification: false,
      notificationVariant: 'error',
      notificationMessage: '',
      tabValue: 0,
      endpoint: null,
      profiles: [],
      events: [],
      eventsPage: 1,
      eventsCount: 0,
      eventsPageSize: 20,
      statusFilter: [],
      dateFrom: null,
      dateTo: null,
      references: [],
      templates: [],
      createProfileDialogOpen: false,
      templateDialogOpen: false,
      newProfileName: '',
      newProfileEventId: null,
      providerEvents: [],
      eventDetail: null,
      eventDetailOpen: false,
      eventPayloadTab: 0,
      eventDetailLoading: false,
      // Settings tab edit mode
      editMode: false,
      editName: '',
      editDescription: '',
      // Regenerate secret
      regenerateConfirmOpen: false,
      regeneratedSecret: null,
      regenerateSecretDialogOpen: false,
      // Template selection
      selectedTemplate: null,
      // Action loading overlay (for create/save/delete, not initial load)
      actionLoading: false,
    };
    this.closeNotification = this.closeNotification.bind(this);
  }

  componentDidMount() {
    this.loadAll();
  }

  loadAll = () => {
    this.setState({ loading: true });
    const { endpointId } = this.props.match.params;

    AppConnectorService.getEndpointById(endpointId).then(epResult => {
      if (!epResult.data.success) { this.setState({ loading: false }); return; }

      const endpoint = normalizeEndpoint(epResult.data.data);
      const pid = endpoint.appConnectorProviderId || endpoint.appconnectorproviderid || endpoint.externalProviderId || endpoint.externalproviderid;

      const promises = [AppConnectorService.getProfiles(endpointId)];
      if (pid) {
        promises.push(AppConnectorService.getProviderEvents(pid));
        promises.push(AppConnectorService.getProviderTemplates(pid));
      }

      Promise.all(promises).then(results => {
        const profiles = results[0]?.data?.success ? (results[0].data.data || []).map(normalizeProfile) : [];
        const providerEvents = results[1]?.data?.success ? (results[1].data.data || []).map(normalizeProviderEvent) : [];
        const templates = results[2]?.data?.success ? (results[2].data.data || []).map(normalizeTemplate) : (Array.isArray(results[2]?.data) ? results[2].data.map(normalizeTemplate) : []);

        this.setState({
          loading: false, endpoint, profiles, providerEvents, templates,
          editName: endpoint.name || '', editDescription: endpoint.description || ''
        });
      }).catch(() => {
        this.setState({ loading: false, endpoint, editName: endpoint.name || '', editDescription: endpoint.description || '' });
      });
    }).catch(err => {
      this.setState({ loading: false });
      Api.kickoff(err);
    });
  }

  loadEndpoint = () => {
    const { endpointId } = this.props.match.params;
    AppConnectorService.getEndpointById(endpointId)
      .then(result => {
        if (result.data.success) {
          const ep = result.data.data;
          const normalized = normalizeEndpoint(ep);
          this.setState({ endpoint: normalized, editName: normalized.name || '', editDescription: normalized.description || '' });
          const epPid = ep.appConnectorProviderId || ep.appconnectorproviderid || ep.externalProviderId;
          if (epPid) {
            AppConnectorService.getProviderEvents(epPid).then(r => {
              if (r.data.success) this.setState({ providerEvents: (r.data.data || []).map(normalizeProviderEvent) });
            });
            AppConnectorService.getProviderTemplates(epPid).then(r => {
              if (r.data.success) this.setState({ templates: (r.data.data || []).map(normalizeTemplate) });
            });
          }
        }
      }).catch(err => Api.kickoff(err));
  }

  loadProfiles = () => {
    const { endpointId } = this.props.match.params;
    AppConnectorService.getProfiles(endpointId).then(result => {
      if (result.data.success) this.setState({ profiles: (result.data.data || []).map(normalizeProfile) });
    }).catch(() => {});
  }

  loadEvents = (page = 1) => {
    const { endpointId } = this.props.match.params;
    const params = { page, pageSize: this.state.eventsPageSize };
    if (this.state.statusFilter.length > 0) params.status = this.state.statusFilter;
    if (this.state.dateFrom) params.dateFrom = moment(this.state.dateFrom).format('YYYY-MM-DD');
    if (this.state.dateTo) params.dateTo = moment(this.state.dateTo).format('YYYY-MM-DD');
    AppConnectorService.getEndpointEvents(endpointId, params).then(result => {
      if (result.data.success) {
        this.setState({ events: (result.data.data || []).map(normalizeEvent), eventsCount: result.data.count || 0, eventsPage: page });
      }
    }).catch(() => {});
  }

  loadReferences = () => {
    const { endpointId } = this.props.match.params;
    AppConnectorService.getEndpointReferences(endpointId).then(result => {
      if (result.data.success) this.setState({ references: result.data.data });
    }).catch(() => {});
  }

  handleTabChange = (e, newValue) => {
    this.setState({ tabValue: newValue });
    if (newValue === 1) this.loadEvents();
    if (newValue === 2) this.loadReferences();
  }

  handleCreateProfile = () => {
    const { endpointId } = this.props.match.params;
    const data = { appConnectorProviderEventId: this.state.newProfileEventId, name: this.state.newProfileName, isActive: true };
    this.setState({ actionLoading: true });
    AppConnectorService.createProfile(endpointId, data).then(result => {
      if (result.data.success) {
        this.setState({ actionLoading: false, createProfileDialogOpen: false, newProfileName: '', newProfileEventId: null,
          openNotification: true, notificationVariant: 'success', notificationMessage: 'Profile criado!' });
        this.loadProfiles();
      } else {
        this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: result.data.errors?.[0] || 'Erro' });
      }
    }).catch(err => {
      this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: err.response?.data?.errors?.[0] || 'Erro' });
    });
  }

  handleUseTemplate = (templateCode) => {
    const { endpointId } = this.props.match.params;
    this.setState({ actionLoading: true });
    AppConnectorService.createProfileFromTemplate(endpointId, templateCode).then(result => {
      if (result.data.success) {
        this.setState({ actionLoading: false, templateDialogOpen: false, selectedTemplate: null,
          openNotification: true, notificationVariant: 'success', notificationMessage: 'Profile criado a partir do template!' });
        this.loadProfiles();
      } else {
        this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: result.data.errors?.[0] || 'Erro' });
      }
    }).catch(err => {
      this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: err.response?.data?.errors?.[0] || 'Erro' });
    });
  }

  handleReplay = (eventId) => {
    const { endpointId } = this.props.match.params;
    AppConnectorService.replayEvent(endpointId, eventId).then(result => {
      this.setState({ openNotification: true, notificationVariant: 'success', notificationMessage: 'Evento reenfileirado!' });
      this.loadEvents(this.state.eventsPage);
    }).catch(() => {});
  }

  handleProfileClick = (profileId) => {
    const { endpointId } = this.props.match.params;
    this.props.history.push(`/${this.props.match.params.lang}/app-connectors/endpoints/${endpointId}/profiles/${profileId}`);
  }

  handleSaveEndpoint = () => {
    const { endpointId } = this.props.match.params;
    this.setState({ actionLoading: true });
    const ep = this.state.endpoint;
    AppConnectorService.updateEndpoint(endpointId, { id: parseInt(endpointId), name: this.state.editName, description: this.state.editDescription || '', isActive: ep?.isActive !== false, isPaused: ep?.isPaused || false })
      .then(result => {
        if (result.data.success) {
          this.setState({ actionLoading: false, editMode: false,
            endpoint: { ...this.state.endpoint, name: this.state.editName, description: this.state.editDescription },
            openNotification: true, notificationVariant: 'success', notificationMessage: 'Endpoint atualizado!' });
        } else {
          this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: result.data.errors?.[0] || 'Erro' });
        }
      }).catch(err => {
        this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: err.response?.data?.errors?.[0] || 'Erro' });
      });
  }

  handleRegenerateSecret = () => {
    const { endpointId } = this.props.match.params;
    this.setState({ regenerateConfirmOpen: false, actionLoading: true });
    AppConnectorService.regenerateSecret(endpointId).then(r => {
      this.setState({ actionLoading: false });
      if (r.data.success) {
        const secret = r.data.data?.signingsecret || r.data.data?.signingSecret || r.data.data?.signing_secret;
        this.setState({ regeneratedSecret: secret, regenerateSecretDialogOpen: true });
      } else {
        this.setState({ openNotification: true, notificationVariant: 'error', notificationMessage: r.data.errors?.[0] || 'Erro ao regenerar secret' });
      }
    }).catch(err => {
      this.setState({ actionLoading: false, openNotification: true, notificationVariant: 'error', notificationMessage: err.response?.data?.errors?.[0] || 'Erro ao regenerar secret' });
    });
  }

  handleEventClick = (evt) => {
    const { endpointId } = this.props.match.params;
    this.setState({ eventDetailOpen: true, eventDetail: evt, eventDetailLoading: true, eventPayloadTab: 0 });
    // Load full event data by ID to get raw_payload
    AppConnectorService.getEventById(endpointId, evt.id)
      .then(result => {
        if (result.data.success) {
          this.setState({ eventDetail: normalizeEvent(result.data.data), eventDetailLoading: false });
        } else {
          this.setState({ eventDetailLoading: false });
        }
      })
      .catch(() => this.setState({ eventDetailLoading: false }));
  }

  getProcessingTimeMs = (evt) => {
    if (evt.processedAt && evt.processingStartedAt) {
      const diff = moment(evt.processedAt).diff(moment(evt.processingStartedAt));
      return diff >= 0 ? diff : null;
    }
    return null;
  }

  closeNotification() { this.setState({ openNotification: false }); }

  renderSkeleton() {
    return (
      <div>
        {/* Title skeleton */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
          <Skeleton animation="wave" variant="circular" width={36} height={36} />
          <Skeleton animation="wave" variant="rectangular" width={250} height={32} sx={{ borderRadius: '6px' }} />
          <Skeleton animation="wave" variant="rectangular" width={60} height={24} sx={{ borderRadius: '12px' }} />
        </div>

        {/* URL bar skeleton */}
        <Skeleton animation="wave" variant="rectangular" width="100%" height={40} sx={{ borderRadius: '8px', mb: 2 }} />

        {/* Stat cards skeleton */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
          <Skeleton animation="wave" variant="rectangular" sx={{ flex: 1, borderRadius: '10px' }} height={90} />
          <Skeleton animation="wave" variant="rectangular" sx={{ flex: 1, borderRadius: '10px' }} height={90} />
          <Skeleton animation="wave" variant="rectangular" sx={{ flex: 1, borderRadius: '10px' }} height={90} />
        </Box>

        {/* Tab bar skeleton */}
        <Skeleton animation="wave" variant="rectangular" width="100%" height={40} sx={{ borderRadius: '6px' }} />
      </div>
    );
  }

  render() {
    const { classes, headerMenu, actionsHeaderMenu } = this.props;
    const { endpoint, tabValue, profiles, events, references, templates, providerEvents } = this.state;
    const totalPages = Math.ceil(this.state.eventsCount / this.state.eventsPageSize);
    const disabledInputProps = { style: { backgroundColor: '#ebebeb', color: '#777', borderRadius: '4px' } };
    const enabledInputProps = { style: { backgroundColor: '#fff' } };

    return (
      <div className={classes.root} style={{ paddingLeft: 0, marginLeft: 0 }}>
        <HeaderMenu />
        <main className={clsx(classes.content, { [classes.contentShift]: headerMenu.open }, classes.actionsContent, { [classes.actionscontentShift]: actionsHeaderMenu.open })}>
          <div className={classes.drawerHeader} />
          <Container component='main' maxWidth='xl' style={{ maxWidth: '100%', paddingLeft: 0, marginLeft: 0 }}>
            <CssBaseline />
            <div style={styles.contentWrapper}>
              {this.state.loading && !endpoint ? (
                this.renderSkeleton()
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <IconButton onClick={() => this.props.history.goBack()}><ArrowLeft size={20} /></IconButton>
                    <h1 style={styles.heading}>{endpoint?.name || 'Endpoint'}</h1>
                    {endpoint?.isActive && <Chip label="Ativo" size="small" color="success" />}
                    {endpoint?.isPaused && <Chip label="Pausado" size="small" color="warning" />}
                  </div>

                  {/* Endpoint URL with copy */}
                  {endpoint?.endpointUrl && (
                    <Box sx={{ mb: 2, p: 1.5, background: '#f8faf5', borderRadius: '8px', border: '1px solid #e8f5e9' }}>
                      <span style={{ fontSize: '11px', color: '#666', textTransform: 'uppercase', fontWeight: 600 }}>Webhook URL: </span>
                      <CopyableField value={endpoint.endpointUrl} label="Copiar URL" truncate={false} />
                    </Box>
                  )}

                  {/* Stats cards */}
                  {endpoint && (
                    <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
                      <Box sx={{ flex: 1, p: 2, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#1a1a2e' }}>{endpoint.totalEventsReceived || 0}</div>
                        <div style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Recebidos</div>
                        {endpoint.lastEventReceivedAt && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>Último: {moment(endpoint.lastEventReceivedAt).fromNow()}</div>}
                      </Box>
                      <Box sx={{ flex: 1, p: 2, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#2e7d32' }}>{endpoint.totalEventsProcessed || 0}</div>
                        <div style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Processados</div>
                        {endpoint.lastEventProcessedAt && <div style={{ fontSize: '11px', color: '#aaa', marginTop: '4px' }}>Último: {moment(endpoint.lastEventProcessedAt).fromNow()}</div>}
                      </Box>
                      <Box sx={{ flex: 1, p: 2, background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', textAlign: 'center' }}>
                        <div style={{ fontSize: '24px', fontWeight: 700, color: '#c62828' }}>{endpoint.totalEventsFailed || 0}</div>
                        <div style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase' }}>Falhas</div>
                      </Box>
                    </Box>
                  )}

                  <Tabs value={tabValue} onChange={this.handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                    <Tab label={`Mapeamentos (${profiles.length})`} />
                    <Tab label="Log de Eventos" />
                    <Tab label="Referências" />
                    <Tab label="Dados" />
                  </Tabs>

                  {/* TAB 0: Profiles */}
                  <TabPanel value={tabValue} index={0}>
                    {profiles.length > 0 && (
                      <>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
                          <Button variant="contained" startIcon={<Plus size={16} />} sx={{ textTransform: 'none', borderRadius: '8px' }}
                            onClick={() => this.setState({ createProfileDialogOpen: true })}>Novo Mapeamento</Button>
                          {templates.length > 0 && (
                            <Button variant="outlined" startIcon={<SquareChartGantt size={16} />} sx={{ textTransform: 'none', borderRadius: '8px' }}
                              onClick={() => this.setState({ templateDialogOpen: true })}>Usar Template</Button>
                          )}
                        </div>

                        {profiles.map(profile => (
                          <div key={profile.id}
                            style={{ ...styles.profileCard, ...(profile.isActive ? styles.profileCardActive : styles.profileCardInactive) }}
                            onClick={() => this.handleProfileClick(profile.id)}>
                            <div>
                              <span style={{ fontWeight: 600, color: '#1a1a2e' }}>{profile.name}</span>
                              <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                                Evento: {(() => {
                                  const evId = profile.appConnectorProviderEventId;
                                  const ev = providerEvents.find(e => e.id === evId || e.id == evId);
                                  return ev ? `${ev.eventName} (${ev.eventCode})` : (profile.eventCode || profile.eventName || `#${evId || ''}`);
                                })()}
                              </div>
                              <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                                {(profile.ruleCount != null) && (
                                  <Chip label={`${profile.ruleCount || 0} regras`} size="small" variant="outlined" sx={{ fontSize: '10px' }} />
                                )}
                                {(profile.triggerCount != null) && (
                                  <Chip label={`${profile.triggerCount || 0} ações`} size="small" variant="outlined" sx={{ fontSize: '10px' }} />
                                )}
                              </div>
                            </div>
                            <Chip label={profile.isActive ? 'Ativo' : 'Inativo'} size="small" color={profile.isActive ? 'success' : 'default'} />
                          </div>
                        ))}
                      </>
                    )}
                    {profiles.length === 0 && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '60px 20px' }}>
                        <div style={{ maxWidth: 440, textAlign: 'center', padding: '40px', background: '#fff', borderRadius: '16px', border: '1px solid #e0e0e0', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                          <Settings size={56} color="#bdbdbd" style={{ marginBottom: '16px' }} />
                          <h3 style={{ fontSize: '18px', fontWeight: 600, color: '#333', marginBottom: '8px' }}>Nenhum mapeamento configurado</h3>
                          <p style={{ fontSize: '14px', color: '#888', lineHeight: 1.6, marginBottom: '24px' }}>
                            Mapeamentos definem como os dados recebidos do sistema externo são transformados em ações no Mexx.
                          </p>
                          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                            <Button variant="contained" startIcon={<Plus size={16} />} onClick={() => this.setState({ createProfileDialogOpen: true })} sx={{ textTransform: 'none', borderRadius: '8px' }}>Criar do Zero</Button>
                            {templates.length > 0 && <Button variant="outlined" startIcon={<SquareChartGantt size={16} />} onClick={() => this.setState({ templateDialogOpen: true })} sx={{ textTransform: 'none', borderRadius: '8px' }}>Usar Template</Button>}
                          </div>
                        </div>
                      </div>
                    )}
                  </TabPanel>

                  {/* TAB 1: Event Log */}
                  <TabPanel value={tabValue} index={1}>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px', alignItems: 'center' }}>
                      {['COMPLETED', 'FAILED', 'SKIPPED', 'PROCESSING', 'RECEIVED'].map(s => (
                        <Chip key={s} label={s} size="small" clickable variant={this.state.statusFilter.includes(s) ? 'filled' : 'outlined'}
                          sx={styles.statusChip[s] || {}}
                          onClick={() => {
                            const f = this.state.statusFilter.includes(s) ? this.state.statusFilter.filter(x => x !== s) : [...this.state.statusFilter, s];
                            this.setState({ statusFilter: f }, () => this.loadEvents(1));
                          }} />
                      ))}
                      <span style={{ borderLeft: '1px solid #ddd', height: '24px', margin: '0 4px' }} />
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                        <DatePicker
                          label="De"
                          value={this.state.dateFrom}
                          onChange={(val) => this.setState({ dateFrom: val }, () => this.loadEvents(1))}
                          renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                        <DatePicker
                          label="Até"
                          value={this.state.dateTo}
                          onChange={(val) => this.setState({ dateTo: val }, () => this.loadEvents(1))}
                          renderInput={(params) => <TextField {...params} size="small" sx={{ width: 160 }} />}
                        />
                      </LocalizationProvider>
                    </div>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Timestamp</TableCell><TableCell>Evento</TableCell><TableCell>Status</TableCell><TableCell>Tempo (ms)</TableCell><TableCell>Ações</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {events.map(evt => {
                            const procTime = this.getProcessingTimeMs(evt);
                            return (
                              <TableRow key={evt.id} hover sx={{ cursor: 'pointer' }}
                                onClick={() => this.handleEventClick(evt)}>
                                <TableCell>{moment(evt.receivedAt).format('DD/MM/YY HH:mm:ss')}</TableCell>
                                <TableCell>{evt.externalEventType || '-'}</TableCell>
                                <TableCell><StatusChip status={evt.status} /></TableCell>
                                <TableCell>{procTime != null ? `${procTime}ms` : '-'}</TableCell>
                                <TableCell>
                                  {(evt.status === 'FAILED' || evt.status === 'DEAD_LETTER') && (
                                    <Tooltip title="Reprocessar"><IconButton size="small" onClick={e => { e.stopPropagation(); this.handleReplay(evt.id); }}><RefreshCw size={14} /></IconButton></Tooltip>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                          {events.length === 0 && (<TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Nenhum evento registrado</TableCell></TableRow>)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    {totalPages > 1 && (<Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                      <Pagination count={totalPages} page={this.state.eventsPage} onChange={(e, p) => this.loadEvents(p)} /></Box>)}
                  </TabPanel>

                  {/* TAB 2: References */}
                  <TabPanel value={tabValue} index={2}>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '10px' }}>
                      <Table size="small">
                        <TableHead><TableRow>
                          <TableCell>Tipo Externo</TableCell><TableCell>ID Externo</TableCell><TableCell>Tipo Interno</TableCell><TableCell>ID Interno</TableCell><TableCell>Criado em</TableCell>
                        </TableRow></TableHead>
                        <TableBody>
                          {references.map(ref => (
                            <TableRow key={ref.id}>
                              <TableCell>{ref.externalEntityType}</TableCell><TableCell sx={{ fontFamily: 'monospace' }}>{ref.externalEntityId}</TableCell>
                              <TableCell>{ref.internalEntityType}</TableCell><TableCell>{ref.internalEntityId}</TableCell>
                              <TableCell>{moment(ref.createdAt).format('DD/MM/YY HH:mm')}</TableCell>
                            </TableRow>
                          ))}
                          {references.length === 0 && (<TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Nenhuma referência</TableCell></TableRow>)}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </TabPanel>

                  {/* TAB 3: Settings / Dados */}
                  <TabPanel value={tabValue} index={3}>
                    <Box sx={{ maxWidth: 600 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                        {!this.state.editMode ? (
                          <Button variant="outlined" size="small" startIcon={<Edit size={14} />} sx={{ textTransform: 'none' }}
                            onClick={() => this.setState({ editMode: true, editName: endpoint?.name || '', editDescription: endpoint?.description || '' })}>
                            Editar
                          </Button>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Button variant="outlined" size="small" sx={{ textTransform: 'none' }}
                              onClick={() => this.setState({ editMode: false })}>
                              Cancelar
                            </Button>
                            <Button variant="contained" size="small" startIcon={<Save size={14} />} sx={{ textTransform: 'none' }}
                              onClick={this.handleSaveEndpoint}>
                              Salvar
                            </Button>
                          </Box>
                        )}
                      </Box>
                      <TextField label="Nome" fullWidth margin="normal"
                        value={this.state.editMode ? this.state.editName : (endpoint?.name || '')}
                        disabled={!this.state.editMode}
                        InputProps={this.state.editMode ? enabledInputProps : disabledInputProps}
                        onChange={e => this.setState({ editName: e.target.value })} />
                      <TextField label="Descrição" fullWidth margin="normal" multiline rows={2}
                        value={this.state.editMode ? this.state.editDescription : (endpoint?.description || '')}
                        disabled={!this.state.editMode}
                        InputProps={this.state.editMode ? enabledInputProps : disabledInputProps}
                        onChange={e => this.setState({ editDescription: e.target.value })} />
                      <Box sx={{ mt: 2 }}>
                        <Button variant="outlined" color="warning" startIcon={<RefreshCw size={16} />}
                          onClick={() => this.setState({ regenerateConfirmOpen: true })}
                          sx={{ textTransform: 'none' }}>
                          Regerar Secret
                        </Button>
                      </Box>
                    </Box>
                  </TabPanel>
                </>
              )}
            </div>

            {/* Regenerate Secret Confirmation Dialog */}
            <Dialog open={this.state.regenerateConfirmOpen} onClose={() => this.setState({ regenerateConfirmOpen: false })} maxWidth="xs" fullWidth>
              <DialogTitle sx={{ pb: 0 }}>Regerar Secret</DialogTitle>
              <DialogContent>
                <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '8px', paddingTop: '16px' }}>
                <Alert severity="warning" sx={{ borderRadius: '8px' }}>
                  <strong>Atenção:</strong> A chave anterior será invalidada imediatamente. Todos os sistemas que usam a chave atual deixarão de funcionar até que a nova chave seja configurada.
                </Alert>
                </div>
              </DialogContent>
              <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={() => this.setState({ regenerateConfirmOpen: false })} sx={{ textTransform: 'none' }}>Cancelar</Button>
                <Button variant="contained" color="warning" onClick={this.handleRegenerateSecret} sx={{ textTransform: 'none' }}>Regerar</Button>
              </DialogActions>
            </Dialog>

            {/* Regenerated Secret Display Dialog */}
            <Dialog open={this.state.regenerateSecretDialogOpen} onClose={() => this.setState({ regenerateSecretDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>Novo Secret Gerado</DialogTitle>
              <DialogContent>
                <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '8px' }}>
                <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
                  <strong>Atenção:</strong> Copie o secret agora. Ele não será exibido novamente.
                </Alert>
                {this.state.regeneratedSecret && (
                  <Box sx={{ p: 2, background: '#f5f5f5', borderRadius: '8px', border: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                    <code style={{ flex: 1, wordBreak: 'break-all', fontSize: '13px' }}>{this.state.regeneratedSecret}</code>
                    <Tooltip title="Copiar secret">
                      <IconButton size="small" onClick={() => {
                        navigator.clipboard.writeText(this.state.regeneratedSecret);
                        this.setState({ openNotification: true, notificationVariant: 'success', notificationMessage: 'Secret copiado!' });
                      }}>
                        <Copy size={16} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ regenerateSecretDialogOpen: false, regeneratedSecret: null })} sx={{ textTransform: 'none' }}>Fechar</Button>
              </DialogActions>
            </Dialog>

            {/* Create Profile Dialog */}
            <Dialog open={this.state.createProfileDialogOpen} onClose={() => this.setState({ createProfileDialogOpen: false })} maxWidth="sm" fullWidth>
              <DialogTitle>Novo Mapeamento</DialogTitle>
              <DialogContent>
                <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '8px' }}>
                <TextField label="Nome do mapeamento" fullWidth margin="normal" value={this.state.newProfileName}
                  onChange={e => this.setState({ newProfileName: e.target.value })} />
                <TextField select label="Evento do Provider" fullWidth margin="normal" SelectProps={{ native: true }}
                  value={this.state.newProfileEventId || ''} onChange={e => this.setState({ newProfileEventId: parseInt(e.target.value) })}
                  InputLabelProps={{ shrink: true }}>
                  <option value="">Selecione um evento...</option>
                  {providerEvents.map(ev => (<option key={ev.id} value={ev.id}>{ev.eventName || ''} ({ev.eventCode || ''})</option>))}
                </TextField>
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ createProfileDialogOpen: false })}>Cancelar</Button>
                <Button variant="contained" onClick={this.handleCreateProfile} disabled={!this.state.newProfileName || !this.state.newProfileEventId}>Criar</Button>
              </DialogActions>
            </Dialog>

            {/* Template Dialog - select then create */}
            <Dialog open={this.state.templateDialogOpen} onClose={() => this.setState({ templateDialogOpen: false, selectedTemplate: null })} maxWidth="sm" fullWidth>
              <DialogTitle>Usar Template Pré-pronto</DialogTitle>
              <DialogContent>
                <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '8px' }}>
                {templates.map(t => (
                  <div key={t.id}
                    style={{
                      ...styles.templateCard,
                      border: this.state.selectedTemplate?.id === t.id ? '2px solid #8BC34A' : styles.templateCard.border,
                      background: this.state.selectedTemplate?.id === t.id ? '#f1f8e9' : styles.templateCard.background,
                    }}
                    onClick={() => this.setState({ selectedTemplate: t })}>
                    <span style={{ fontWeight: 600 }}>{t.templateName || 'Template'}</span>
                    <p style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>{t.templateDescription || ''}</p>
                    <Chip label={t.eventCode || ''} size="small" sx={{ mt: 1, fontSize: '11px' }} />
                  </div>
                ))}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => this.setState({ templateDialogOpen: false, selectedTemplate: null })}>Cancelar</Button>
                <Button variant="contained" disabled={!this.state.selectedTemplate}
                  onClick={() => this.handleUseTemplate(this.state.selectedTemplate.templateCode)}>
                  Criar com Template
                </Button>
              </DialogActions>
            </Dialog>

            {/* Event Detail Dialog with PayloadViewer */}
            <Dialog open={this.state.eventDetailOpen} onClose={() => this.setState({ eventDetailOpen: false })} maxWidth="lg" fullWidth>
              <DialogTitle>Evento #{this.state.eventDetail?.id}</DialogTitle>
              <DialogContent>
                <div style={{ overflowY: 'auto', maxHeight: '70vh', paddingRight: '8px' }}>
                {this.state.eventDetailLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>
                )}
                {this.state.eventDetail && !this.state.eventDetailLoading && (
                  <Box>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatusChip status={this.state.eventDetail.status} />
                      <span style={{ fontSize: '13px', color: '#666' }}>Evento: <strong>{this.state.eventDetail.externalEventType || '-'}</strong></span>
                      <span style={{ fontSize: '13px', color: '#666' }}>Recebido: {moment(this.state.eventDetail.receivedAt).format('DD/MM/YYYY HH:mm:ss')}</span>
                      {this.state.eventDetail.processedAt && <span style={{ fontSize: '13px', color: '#666' }}>Processado: {moment(this.state.eventDetail.processedAt).format('DD/MM/YYYY HH:mm:ss')}</span>}
                      {(() => {
                        const pt = this.getProcessingTimeMs(this.state.eventDetail);
                        return pt != null ? <Chip label={`${pt}ms`} size="small" variant="outlined" sx={{ fontSize: '11px' }} /> : null;
                      })()}
                      {this.state.eventDetail.retryCount > 0 && <Chip label={`Tentativas: ${this.state.eventDetail.retryCount}`} size="small" color="warning" />}
                      {this.state.eventDetail.ipAddress && <span style={{ fontSize: '12px', color: '#999' }}>IP: {this.state.eventDetail.ipAddress}</span>}
                    </Box>

                    {this.state.eventDetail.errorMessage && (
                      <Alert severity="error" sx={{ mb: 2 }}>{this.state.eventDetail.errorMessage}</Alert>
                    )}

                    {/* Payload tabs: Raw + Enriched */}
                    <Tabs value={this.state.eventPayloadTab || 0} onChange={(e, v) => this.setState({ eventPayloadTab: v })} sx={{ mb: 1 }}>
                      <Tab label="Raw Payload" sx={{ textTransform: 'none', fontSize: '13px' }} />
                      {this.state.eventDetail.enrichedPayload && <Tab label="Enriched Payload" sx={{ textTransform: 'none', fontSize: '13px' }} />}
                      {this.state.eventDetail.processingResult && <Tab label="Resultado" sx={{ textTransform: 'none', fontSize: '13px' }} />}
                    </Tabs>

                    {(this.state.eventPayloadTab || 0) === 0 && this.state.eventDetail.rawPayload && (
                      <PayloadViewer data={this.state.eventDetail.rawPayload} name="raw_payload" collapsed={2} />
                    )}
                    {(this.state.eventPayloadTab || 0) === 0 && !this.state.eventDetail.rawPayload && (
                      <Alert severity="info" sx={{ mt: 1 }}>Payload não disponível no resumo. O payload completo é carregado ao abrir o detalhe.</Alert>
                    )}
                    {this.state.eventPayloadTab === 1 && this.state.eventDetail.enrichedPayload && (
                      <PayloadViewer data={this.state.eventDetail.enrichedPayload} name="enriched_payload" collapsed={2} />
                    )}
                    {this.state.eventPayloadTab === 2 && this.state.eventDetail.processingResult && (
                      <PayloadViewer data={this.state.eventDetail.processingResult} name="processing_result" collapsed={1} />
                    )}
                  </Box>
                )}
                </div>
              </DialogContent>
              <DialogActions>
                {this.state.eventDetail && (this.state.eventDetail.status === 'FAILED' || this.state.eventDetail.status === 'DEAD_LETTER') && (
                  <Button startIcon={<RefreshCw size={14} />} color="warning"
                    onClick={() => { this.handleReplay(this.state.eventDetail.id); this.setState({ eventDetailOpen: false }); }}>Reprocessar</Button>
                )}
                <Button onClick={() => this.setState({ eventDetailOpen: false })}>Fechar</Button>
              </DialogActions>
            </Dialog>

            {this.state.actionLoading && (<Overlay><CircularProgress color='secondary' /></Overlay>)}
            <CustomizedSnackbars variant={this.state.notificationVariant} message={this.state.notificationMessage}
              isOpen={this.state.openNotification} toClose={this.closeNotification} />
          </Container>
        </main>
        <Footer />
      </div>
    );
  }
}

AppConnectorEndpointDetail.propTypes = { classes: PropTypes.object.isRequired };
const mapStateToProps = state => ({ actionsHeaderMenu: state.actionsHeaderMenu, headerMenu: state.headerMenu, userSession: state.userSession });
const mapDispatchToProps = dispatch => bindActionCreators(componentActions, dispatch);
export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(withStyles(Styles)(AppConnectorEndpointDetail)));
